/**
 * `src/lib/content/model.ts` — THE WORD-SPAN CONTRACT.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * This file is the single place where a character of the Qur'an becomes a word
 * INDEX. Every `ref` this app ever mints, every `quote` it ever stores, every
 * hash the degradation ladder ever compares, and every highlight a user ever
 * draws resolves through `buildAyahModel`. A one-off-by-four here does not
 * produce a visible bug; it produces notes that point at the wrong words and
 * keep pointing at the wrong words after every future correction, because the
 * `quote` and the hashes would have been captured over the wrong span too and
 * the ladder would read them as tier 1 forever.
 *
 * So: NOTHING in this file decides what a word is. `segment()`, `isWordToken()`
 * and `canonicalWords()` decide, in `src/lib/anchor/segment.ts`, which is
 * frozen; `wordCountOf()` decides how many there are, out of the committed
 * 6,236-row fixture. This file attaches VERBATIM CHARACTER OFFSETS to that
 * decision and then proves, per ayah, at runtime, that its attachment agrees
 * with the frozen module on every token. Disagreement throws.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * WHY A SCANNER AT ALL, WHEN `segment()` ALREADY SPLITS THE TEXT
 * ─────────────────────────────────────────────────────────────
 * `segment()` returns token STRINGS. A mushaf page has to draw the verbatim
 * text — including the 4,379 standalone waqf and sajdah marks that are NOT
 * words and must not be tappable — so the renderer needs to know where in `t`
 * each token sits, and it needs the material between tokens to survive. A
 * renderer that reconstructed the line by joining `segment()` output would erase
 * every waqf sign in the mushaf.
 *
 * The scanner below therefore walks `t` once, recording `{text, start, end}` for
 * every whitespace-delimited token, and classifies each with the FROZEN
 * `isWordToken()`. It is then asserted — not assumed — that the word tokens it
 * found are element-for-element `segment(t)`, and that dropping the leading `b`
 * of them is element-for-element `canonicalWords(s, a, t)`. If the scanner and
 * the frozen tokeniser ever disagreed about a single token, this module throws
 * `tokeniser_disagreement` rather than render.
 *
 * That is the whole design: the scanner supplies geometry, the frozen module
 * supplies truth, and an equality assertion on every ayah is what keeps the
 * geometry honest. A raw `t.split(/\s+/)` appears nowhere, and could not pass
 * these assertions if it did — it keeps the marks and the basmala.
 */

import {
	BASMALA_FOLDED_WORDS,
	canonicalWords,
	fold,
	isValidAyahId,
	isWordToken,
	posOf,
	segment,
	wordCountOf
} from '../anchor';
import {
	ContentLoadError,
	WordSpanContractError,
	type AyahModel,
	type AyahRow,
	type BasmalaSegment,
	type MarkSegment,
	type RenderSegment,
	type WordSegment
} from './types';

/**
 * The number of ayat in the mushaf whose Tanzil text prepends the basmala: the
 * first ayah of every surah except al-Fatiha (where the basmala genuinely IS
 * ayah 1) and at-Tawbah (which has none). 114 − 2 = 112.
 */
export const BASMALA_ROW_COUNT = 112;

/** The two surahs that must never carry a prepended basmala, by name. */
export const SURAH_AL_FATIHA = 1;
export const SURAH_AT_TAWBAH = 9;

/** The four-word basmala prefix length, in canonical words. */
export const BASMALA_WORD_COUNT = BASMALA_FOLDED_WORDS.length;

/**
 * Whitespace, as a SINGLE-CHARACTER test.
 *
 * The frozen tokeniser splits on `/\s+/u` after `String.prototype.trim()`. This
 * regex is the same character class, deliberately non-global (a `/g/` regex
 * carries `lastIndex` between `.test()` calls, which is how a scanner silently
 * skips every other token).
 */
const SPACE = /\s/u;

/** A whitespace-delimited token of the verbatim text, with its offsets. */
interface ScannedToken {
	text: string;
	start: number;
	end: number;
}

/**
 * Split `text` into whitespace-delimited tokens, keeping each token's offsets.
 * Pure. The concatenation of the tokens and the gaps between them is `text`.
 *
 * Exported for the tests, which assert its equivalence to the frozen
 * `segment()` over all 6,236 ayat rather than trusting this comment.
 */
export function scanTokens(text: string): ScannedToken[] {
	const out: ScannedToken[] = [];
	const n = text.length;
	let i = 0;
	while (i < n) {
		while (i < n && SPACE.test(text.charAt(i))) i++;
		if (i >= n) break;
		const start = i;
		while (i < n && !SPACE.test(text.charAt(i))) i++;
		out.push({ text: text.slice(start, i), start, end: i });
	}
	return out;
}

/** `true` iff the two string arrays are element-for-element equal. */
function sameTokens(a: readonly string[], b: readonly string[]): boolean {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
	return true;
}

/** Human-readable identity of a row, for error messages. */
function rowWhere(row: Pick<AyahRow, 'i' | 's' | 'a'>): string {
	return `ayahId ${row.i} — ${row.s}:${row.a}`;
}

/**
 * Structural validation of a corpus row, before anything is derived from it.
 * A row that fails this is a malformed FILE, not a contract violation, so it
 * raises `ContentLoadError`.
 */
export function assertRowShape(row: unknown, where: string): asserts row is AyahRow {
	if (typeof row !== 'object' || row === null) {
		throw new ContentLoadError('malformed_shape', where, 'an ayah row must be an object');
	}
	const r = row as Record<string, unknown>;
	for (const field of ['i', 's', 'a', 'w'] as const) {
		if (!Number.isInteger(r[field])) {
			throw new ContentLoadError(
				'malformed_shape',
				where,
				`ayah row field "${field}" must be an integer, got ${JSON.stringify(r[field])}`
			);
		}
	}
	if (typeof r['t'] !== 'string' || r['t'] === '') {
		throw new ContentLoadError(
			'malformed_shape',
			where,
			'ayah row field "t" must be a non-empty string'
		);
	}
	if (!isValidAyahId(r['i'])) {
		throw new ContentLoadError(
			'malformed_shape',
			where,
			`ayahId ${String(r['i'])} is not in 1..6236`
		);
	}
	if (r['b'] !== undefined && !Number.isInteger(r['b'])) {
		throw new ContentLoadError(
			'malformed_shape',
			where,
			`ayah row field "b" must be an integer when present, got ${JSON.stringify(r['b'])}`
		);
	}
}

/**
 * Build the render model of one ayah — the word-span contract, enforced.
 *
 * The order of operations is the contract, and it is worth reading as a list:
 *
 *   1. scan `t` into tokens with offsets;
 *   2. classify each with the FROZEN `isWordToken()`;
 *   3. prove the word tokens are exactly `segment(t)`;
 *   4. derive the basmala prefix length as
 *      `segment(t).length − canonicalWords(s, a, t).length`
 *      — DERIVED from the frozen basmala rule, never from the position of the
 *      tokens and never from `b` alone — then prove it equals `b ?? 0`;
 *   5. prove the prefix tokens fold to exactly `BASMALA_FOLDED_WORDS`;
 *   6. index the REMAINING word tokens 0..w−1 and stamp each with
 *      `posOf(ayahId, index)`;
 *   7. prove `words.length === row.w === wordCountOf(ayahId)`;
 *   8. prove the segments reassemble to `t` with nothing lost.
 *
 * Step 4 is the one that matters. `b` is data in a file; the basmala rule is
 * code in a frozen contract. Trusting `b` would mean a mis-emitted `b` shifts
 * 112 ayat by four with nothing to catch it. Deriving the prefix from
 * `canonicalWords()` and then checking `b` against it makes `b` a redundant
 * cross-check, which is the only thing a field like that should ever be.
 */
export function buildAyahModel(row: AyahRow): AyahModel {
	const where = rowWhere(row);
	const text = row.t;

	/* 1–2. geometry, classified by the frozen predicate. */
	const scanned = scanTokens(text);
	const wordTokens: ScannedToken[] = [];
	const markTokens: ScannedToken[] = [];
	for (const token of scanned) {
		if (isWordToken(token.text)) wordTokens.push(token);
		else markTokens.push(token);
	}

	/* 3. the scanner and the frozen tokeniser must agree, token for token. */
	const frozenTokens = segment(text);
	if (
		!sameTokens(
			wordTokens.map((t) => t.text),
			frozenTokens
		)
	) {
		throw new WordSpanContractError(
			'tokeniser_disagreement',
			where,
			`the offset scanner found ${wordTokens.length} word tokens but segment() found ` +
				`${frozenTokens.length}; the frozen tokenisation is the authority and the render ` +
				'model may not diverge from it'
		);
	}

	/* 4. the basmala prefix, DERIVED from the frozen basmala rule. */
	const canonical = canonicalWords(row.s, row.a, text);
	const prefixLength = frozenTokens.length - canonical.length;
	const declared = row.b ?? 0;
	if (prefixLength !== declared) {
		throw new WordSpanContractError(
			'basmala_mismatch',
			where,
			`the frozen basmala rule strips ${prefixLength} leading token(s) but the row declares ` +
				`b = ${declared}. Canonical word 0 is the first word AFTER the basmala; the corpus ` +
				'and the anchor space disagree about where that is'
		);
	}
	if (prefixLength !== 0 && prefixLength !== BASMALA_WORD_COUNT) {
		throw new WordSpanContractError(
			'basmala_mismatch',
			where,
			`a basmala prefix is ${BASMALA_WORD_COUNT} words or none; got ${prefixLength}`
		);
	}
	/* THE TWO EXCLUDED SURAHS ARE NOT CHECKED HERE, AND THAT IS DELIBERATE.
	 * `hasPrependedBasmala` refuses surah 1 (where the basmala genuinely IS
	 * ayah 1, four canonical words) and surah 9 (which has none) whatever the
	 * file says, so `prefixLength` is 0 for both and a `b: 4` on either is
	 * already caught, by name, one check above: the DERIVATION wins and the
	 * file loses. A second guard here would be a branch no input can reach —
	 * unreachable code in a contract is a claim nothing tests. The corpus-wide
	 * version of the check, which IS reachable and IS tested, is
	 * `assertBasmalaCensus` at the bottom of this file. */

	/* 5. the prefix must BE the basmala, by fold — not by position. */
	let basmala: BasmalaSegment | null = null;
	if (prefixLength > 0) {
		const prefix = wordTokens.slice(0, prefixLength);
		for (const [k, expected] of BASMALA_FOLDED_WORDS.entries()) {
			const actual = fold((prefix[k] as ScannedToken).text);
			if (actual !== expected) {
				throw new WordSpanContractError(
					'basmala_mismatch',
					where,
					`basmala token ${k} folds to ${JSON.stringify(actual)}, expected ` +
						`${JSON.stringify(expected)}`
				);
			}
		}
		const first = prefix[0] as ScannedToken;
		const last = prefix[prefixLength - 1] as ScannedToken;
		/* Nothing may sit between the basmala tokens: a mark token inside the
		 * prefix would mean the header a renderer draws is not the contiguous
		 * slice this segment claims it is. */
		for (const mark of markTokens) {
			if (mark.start < last.end) {
				throw new WordSpanContractError(
					'basmala_mismatch',
					where,
					`a non-word token ${JSON.stringify(mark.text)} sits inside the basmala prefix`
				);
			}
		}
		basmala = {
			kind: 'basmala',
			anchorable: false,
			text: text.slice(first.start, last.end),
			start: first.start,
			end: last.end,
			tokens: Object.freeze(prefix.map((t) => t.text))
		};
	}

	/* 6. the canonical words, indexed 0..w−1. THE anchorable segments. */
	const anchorable = wordTokens.slice(prefixLength);
	if (
		!sameTokens(
			anchorable.map((t) => t.text),
			canonical
		)
	) {
		throw new WordSpanContractError(
			'tokeniser_disagreement',
			where,
			'the words left after the basmala strip are not canonicalWords(); the render model ' +
				'would index a different array than the anchor space does'
		);
	}
	const words: WordSegment[] = anchorable.map((token, index) => ({
		kind: 'word',
		anchorable: true,
		text: token.text,
		start: token.start,
		end: token.end,
		index,
		pos: posOf(row.i, index)
	}));

	/* 7. the count, against the file AND against the frozen fixture. */
	const frozenCount = wordCountOf(row.i);
	if (words.length !== row.w || words.length !== frozenCount) {
		throw new WordSpanContractError(
			'word_count_mismatch',
			where,
			`anchorable word count ${words.length} must equal the row's w = ${row.w} and the ` +
				`frozen fixture's wordCountOf() = ${frozenCount}`
		);
	}

	/* 8. the segments, in source order, reassembling to `t`. */
	const marks: MarkSegment[] = markTokens
		.filter((token) => basmala === null || token.start >= basmala.end)
		.map((token) => ({
			kind: 'mark',
			anchorable: false,
			text: token.text,
			start: token.start,
			end: token.end
		}));
	const segments: RenderSegment[] = [...(basmala ? [basmala] : []), ...words, ...marks].sort(
		(a, b) => a.start - b.start
	);
	assertSegmentsCoverText(segments, text, where);

	return {
		ayahId: row.i,
		surah: row.s,
		ayah: row.a,
		text,
		segments: Object.freeze(segments),
		words: Object.freeze(words),
		wordCount: words.length,
		basmala
	};
}

/**
 * Prove the segmentation is LOSSLESS: segments are in order, do not overlap,
 * their `text` is the verbatim slice they claim, and everything between them is
 * whitespace only.
 *
 * This is what makes it safe for a renderer to draw the model instead of the
 * string. Without it, a bug that dropped a waqf mark from the model would show
 * up as a subtly wrong mushaf page and nowhere else.
 */
function assertSegmentsCoverText(
	segments: readonly RenderSegment[],
	text: string,
	where: string
): void {
	let cursor = 0;
	for (const segment_ of segments) {
		if (segment_.start < cursor || segment_.end <= segment_.start || segment_.end > text.length) {
			throw new WordSpanContractError(
				'segmentation_not_lossless',
				where,
				`segment [${segment_.start}, ${segment_.end}) is out of order or out of bounds`
			);
		}
		const gap = text.slice(cursor, segment_.start);
		if (gap !== '' && gap.trim() !== '') {
			throw new WordSpanContractError(
				'segmentation_not_lossless',
				where,
				`non-whitespace text ${JSON.stringify(gap)} at offset ${cursor} belongs to no segment`
			);
		}
		if (text.slice(segment_.start, segment_.end) !== segment_.text) {
			throw new WordSpanContractError(
				'segmentation_not_lossless',
				where,
				`segment at offset ${segment_.start} does not match the text it claims`
			);
		}
		cursor = segment_.end;
	}
	const tail = text.slice(cursor);
	if (tail !== '' && tail.trim() !== '') {
		throw new WordSpanContractError(
			'segmentation_not_lossless',
			where,
			`non-whitespace text ${JSON.stringify(tail)} after the last segment belongs to no segment`
		);
	}
}

/**
 * The corpus-wide basmala census, as a runtime invariant.
 *
 * Per-ayah checks cannot see this one: a build that emitted `b` on 111 rows
 * instead of 112 would pass `buildAyahModel` on every row it emitted and simply
 * shift one surah's opening ayah by four words. Only a walk of the whole corpus
 * can catch it, so the loader offers a walk (`verifyCorpus`) and this is what it
 * checks at the end.
 *
 * Throws on anything but exactly `BASMALA_ROW_COUNT` rows, and names al-Fatiha
 * and at-Tawbah explicitly rather than relying on the count alone: 112 rows with
 * the wrong two surahs in the set is still 112.
 */
export function assertBasmalaCensus(surahsWithBasmala: Iterable<number>, where: string): void {
	const surahs = new Set(surahsWithBasmala);
	if (surahs.has(SURAH_AL_FATIHA)) {
		throw new WordSpanContractError(
			'basmala_mismatch',
			where,
			'surah 1 (al-Fatiha) carries a prepended basmala — it must not: there the basmala IS ' +
				'ayah 1 and its four words are canonical words 0..3'
		);
	}
	if (surahs.has(SURAH_AT_TAWBAH)) {
		throw new WordSpanContractError(
			'basmala_mismatch',
			where,
			'surah 9 (at-Tawbah) carries a prepended basmala — it must not: at-Tawbah has none'
		);
	}
	if (surahs.size !== BASMALA_ROW_COUNT) {
		throw new WordSpanContractError(
			'basmala_mismatch',
			where,
			`${surahs.size} surahs carry a prepended basmala, expected exactly ${BASMALA_ROW_COUNT} ` +
				'(114 surahs, less al-Fatiha and at-Tawbah)'
		);
	}
}
